import { toast } from 'sonner';
import React, { useRef, useEffect, useCallback } from 'react';

import { useTranslate } from 'src/locales';
import { useChannelContext } from 'src/pages/channel/hooks';
import { GOOGLE_SCOPES, GOOGLE_CLIENT_ID, GOOGLE_REDIRECT_URI } from 'src/config';

declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initCodeClient: (config: {
            client_id: string;
            scope: string;
            redirect_uri: string;
            ux_mode: string;
            callback: (response: { code: string }) => void;
          }) => { requestCode: () => void };
        };
      };
    };
  }
}

interface GoogleAuthPopupProps {
  Provider?: string;
}

export const useGoogleAuthPopup = ({ Provider = '' }: GoogleAuthPopupProps = {}) => {
  
  // GOOGLE OAUTH2.0

  const { t } = useTranslate('translation');
  const { state: channelState, handleGoogleDetail } = useChannelContext();

  const [isGoogleAccVerified, setIsGoogleAccVerified] = React.useState(false);
  const clientRef = useRef<{ requestCode: () => void } | null>(null);

  const { emailProviderType, googleEmailDetails } = channelState;

  const loginWithGoogle = () => clientRef.current?.requestCode();

  const googleAuthConfig = React.useMemo(
    () => ({
      clientId: GOOGLE_CLIENT_ID,
      redirectUri: GOOGLE_REDIRECT_URI,
      scopes: GOOGLE_SCOPES,
    }),
    []
  );

  //  Global Google
  const initializeGSI = useCallback(() => {
    clientRef.current = window.google.accounts.oauth2.initCodeClient({
      client_id: googleAuthConfig.clientId,
      scope: googleAuthConfig.scopes,
      redirect_uri: googleAuthConfig.redirectUri,
      ux_mode: 'popup',
      callback: (response) => {
        // console.log('(Google) Authorization Code:', response);

        if (!response || !response.code) {
          const failedMsg = t('ACCOUNT_VERIFICATION_FAILED').replace(
            '{EMAIL_ACC}',
            emailProviderType.toUpperCase()
          );
          toast.error(failedMsg);
          return;
        }

        handleGoogleDetail(response?.code ?? '');
        toast.success(
          `${t(emailProviderType.toUpperCase())} ${t('ACCOUNT_VERIFIED_SUCCESSFULLY')}`
        );
      },
    });
  }, [emailProviderType, googleAuthConfig, handleGoogleDetail, t]);

  useEffect(() => {
    if (emailProviderType === 'google' || Provider === 'google') {
      if (window.google && window.google.accounts) {
        initializeGSI();
      } else {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = initializeGSI;
        document.body.appendChild(script);
      }
    }
  }, [emailProviderType, initializeGSI]);

  useEffect(() => {
    if (googleEmailDetails && googleEmailDetails.code) {
      setIsGoogleAccVerified(true);
    }
  }, [googleEmailDetails]);

  return { loginWithGoogle, isGoogleAccVerified };
};
